import { SearchX } from 'lucide-react'
import Button from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { ReactNode, ComponentType } from 'react'

interface EmptyStateProps {
  /** 메인 메시지 */
  message: string
  /** 보조 설명 */
  description?: string
  /** 액션 버튼 라벨 */
  actionLabel?: string
  /** 액션 버튼 클릭 핸들러 */
  onAction?: () => void
  /** 아이콘 (기본: SearchX) */
  icon?: ReactNode
  /**
   * SVG 일러스트 컴포넌트 (EmptySearch, EmptyBookmark 등).
   * icon보다 우선 적용됩니다.
   */
  illustration?: ComponentType<{ width?: number; height?: number; className?: string }>
  /** 추가 CSS 클래스 */
  className?: string
}

/**
 * 빈 결과 화면 컴포넌트.
 *
 * - 검색 결과 없음, 추천 결과 없음 등에 사용
 * - 일러스트 또는 아이콘 + 메시지 + 액션 버튼 구성
 * - illustration prop이 있으면 아이콘 대신 일러스트 표시
 * - 40~60대 사용자 고려: 큰 폰트, 명확한 메시지
 */
export default function EmptyState({
  message,
  description,
  actionLabel,
  onAction,
  icon,
  illustration: Illustration,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-4 py-12 text-center',
        className
      )}
      role="status"
    >
      {/* 일러스트 우선, 없으면 아이콘 */}
      {Illustration ? (
        <Illustration width={180} height={140} />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cb-gray-100">
          {icon ?? <SearchX size={32} className="text-cb-gray-400" />}
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <p className="text-h3 font-semibold text-cb-gray-700">{message}</p>
        {description && (
          <p className="text-body text-cb-gray-500">{description}</p>
        )}
      </div>
      {actionLabel && onAction && (
        <Button variant="secondary" size="md" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
